import { PolicyDialog } from './policyDialog';
import consoleLogger from '../../logger/consoleLogger';
import { GameClient } from '../../gameClient/gameClient';
import { ClientType } from '../../gameClient/clientType';
import { CurrentEntryPoint } from '../../constants/CurrentEntryPoint';
import { Gender } from '../../constants/Gender';
import { PreloaderScreen } from '../../gameClient/webgl/preloaderScreen';
import { Environment } from '../../environment/environment';
import { getBrowserName } from '../../utils/browser';
import { getUserSuid } from './getUserSuid';
import { GetUserError } from './getUserError';
import { callserver } from './callserver';
import { Url } from './url';
import { Localization } from '../../localization/localization';
import { GetUserResponse } from '../../types/server';
import { Stat } from '../../stat/Stat';
import { ClientJS } from 'clientjs';
import { UserFingerprint } from './userFingerpint/UserFingerprint';
import { NotificationsWrap } from './notifications/NotificationsWrap';
import { CurrentUser } from './user/currentUser';
import { isRetentionUser } from './appEvents';
import { CollectedRefsFactSender } from '../../marketing/collected-refs/collected-refs-fact-sender';

const logger = consoleLogger.getPrefixedCopy('GET_USER');

window.app.getUserResponse = null;

function getUserCity(): string {
    let cityRegexp: RegExp;
    let city: string;

    cityRegexp = new RegExp('^[а-яА-Я\\s-.()]{0,50}$|^[a-zA-Z\\s-.()]{0,50}$', 'i');
    city = $.trim(window.app.user.city);
    if ((cityRegexp.test(city)) !== true) {
        city = '';
    }

    return city;
}

function getUserName(): string {
    let name;
    const nameRegexp = new RegExp('^[а-яА-Я\\s]{0,30}$|^[a-zA-Z\\s]{0,30}$', 'i');
    name = window.app.user.full_name;

    if ((nameRegexp.test(name)) !== true) {
        name = '';
    }

    return name;
}

function buildGetUserRequest(): GetUserRequest {
    const userFingerPrint = new UserFingerprint(new ClientJS());
    const user = window.app.user;
    const request: GetUserRequest = {
        age: window.app?.user?.age,
        browser: getBrowserName(),
        city: getUserCity(),
        clientType: window.app.clientType,
        client_id: window.app?.log_cookie ?? (`client_generated_${getUserSuid()}_${Date.now()}`),
        current_entry_point: window.app.config.current_entry_point,
        full_name: getUserName(),
        isMale: user.gender,
        isMember: user.isMember ? 1 : 0,
        ref: window.app.config.ref,
        ref_p: window.app.config.ref_p,
        sig: window.app.config.sig,
        suid: getUserSuid(),
        fingerprintId: userFingerPrint.getId(),
        firstWebVisitTs: userFingerPrint.getTs(),
        firstWebVisitRnd: userFingerPrint.getRnd(),
        deviceId: window.app.deviceIdentifier
    };

    if (user.email) {
        request.email = user.email;
    }

    if (window.app.config.suid_simulate) {
        request.suid_simulate = window.app.config.suid_simulate;
    }

    return request;
}

type GetUserRequest = {
    suid: string,
    ref: string,
    ref_p: string,
    current_entry_point: CurrentEntryPoint,
    age: number | '',
    isMember: 1 | 0,
    isMale: Gender,
    city: string,
    full_name: string,
    sig: string,
    email?: string,
    suid_simulate?: string,
    clientType: ClientType,
    browser: string,
    client_id: string,
    fingerprintId?: number,
    firstWebVisitTs: string,
    firstWebVisitRnd: string,
    deviceId?: string
};

const GET_USER_RETRY_DELAY = 100;

export function getUser(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let request: { [key: string]: any };

    // TODO Wtf, почему тут эта проверка?
    if (!window.app.isDesktop) {
        return;
    }

    try {
        if (window.app.getUserResponse) {
            return;
        }

        if (window.app.flags.user_ready) {
            request = buildGetUserRequest();
            callserver('getuser', request, handleGetUserResponse);
        } else {
            setTimeout(function scheduleGetUserRetry() {
                console.log('window.app.getUser waiting window.app.flags.user_ready');
                getUser();
            }, GET_USER_RETRY_DELAY);
        }
    } catch (error) {
        window.Sentry.captureException(error);
        throw error;
    }
}

function handleGetUserResponse(response: GetUserResponse) {
    try {
        if (response?.result) {
            handleGetUserSuccessResponse(response);
        } else {
            handleGetUserErrorResponse(response);
        }
    } catch (error) {
        logger.error(error);
        window.Sentry.captureException(error);
    }
}

function handleGetUserSuccessResponse(response: GetUserResponse) {
    window.app.config.user_auth_token = response.new_token;
    Stat.activate();
    window.app.config.sig_funnel = response.result.sig_funnel;
    window.app.config.user_realm = 0;
    if (response.result.realmID) {
        window.app.config.user_realm = response.result.realmID;
    }

    if (response.result.sig) {
        window.app.config.sig = response.result.sig;
    }

    window.app.config.stringGuid = '';
    if (response.result.stringGuid) {
        window.app.config.stringGuid = response.result.stringGuid;
    }

    logger.info('get_user', 'window.app.getUser | ready | ', response);

    if (response?.result?.registration === 1) {
        window.app.flags.first_registration = true;
        window.app.eventInstall();
    } else {
        window.app.notInstall();
    }

    isRetentionUser(response);

    window.app.getUserResponse = response;
    PolicyDialog.checkPolicyAccept();

    NotificationsWrap.startSession(CurrentUser.getInstance().getSuid()
        .toString());

    if (response.result.registration === 1) {
        const collectedRefsSender = new CollectedRefsFactSender(window.app.config.collectedRefs, Stat);
        collectedRefsSender.send();
    }
}

function handleGetUserErrorResponse(response: GetUserResponse) {
    window.app.flags.get_user_error = true;

    const errorMessage = response?.error?.message;

    if (typeof errorMessage === 'undefined') {
        logger.error('Invalid get user response', response);
        throw new GetUserError('Invalid getuser response');
    }

    const isNotSpooferError = response.error.message.search(/not spoofer/) !== -1;
    if (isNotSpooferError) {
        PreloaderScreen.getInstance().hide();
        GameClient.hide();
        showNotSpoofer();
    }

    if (String(errorMessage).trim() === '0') {
        throw new GetUserError('Request was blocked');
    }

    throw new GetUserError(errorMessage);
}

function showNotSpoofer() {
    let link: string;
    let linkEl: JQuery;
    const $techWorks = $('#techworks');
    const $techWorksContent = $techWorks.find('#techworksh2');
    const $techWorksContent1 = $techWorks.find('#techworksh1');

    link = '';

    if (Environment.isSocial()) {
        link = window.app.config.app_url + window.app.config.social_app_id;
        linkEl = $('<a href="#">').on('click', function redirectToSocApp(event) {
            event.preventDefault();
            top.location.href = link;
        });
        linkEl.text(Localization._n('not_spoofer_goto'));
        $techWorksContent.text('');
        $techWorksContent.append(linkEl);
    } else {
        link = Url.getSiteNoLangUrl('');
        $techWorksContent.html(`<a href="${link}">${Localization._n('not_spoofer_goto')}</a>`);
    }

    $techWorksContent1.text(Localization._n('not_spoofer_error'));
    $techWorks.show();
}

export function isGetUserFinished() {
    const GET_USER_CHECK_TICK = 200;

    return new Promise((resolve, reject) => {
        const getUserCheckInterval = setInterval(() => {
            if (window.app.flags.user_ready && window.app.flags.get_user_ready) {
                clearInterval(getUserCheckInterval);
                resolve();
                return;
            }

            if (window.app.flags.get_user_error) {
                clearInterval(getUserCheckInterval);
                reject(new GetUserError('Get user failed'));
            }
        }, GET_USER_CHECK_TICK);
    });
}
